import { createState } from "@/global/store/zustand_store";
import { NotificationModal } from "@/shared/common/InfoPopup/NotificationModal";
import { useTheme, Box, Grid, Stack, Typography } from "@mui/material";
import { useCssMediaQuery } from "@/shared/base/hooks/useCssMediaQuery";
import React from "react";
import { Divider } from "@/shared/base/components/Divider/Divider";
import { ModalActionButton } from "@/shared/base/components/ModalActionButton/ModalActionButton";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import { TRecipient, TRecipientStatus, TTransferStatus } from "./types";
import {
  formatUSPhoneNumberWithBracket,
  throttleTime,
} from "@/global/utils/utils";
import EditNoteRoundedIcon from "@mui/icons-material/EditNoteRounded";
import { useTicketTransfer } from "./useTicketTransfer";

const statusOptions: TRecipientStatus[] = [
  { code: "transfer_initiated", value: "Initiate Transfer" },
  { code: "transfer_revoked", value: "Revoke Transfer" },
];

export const useUpdateRecipientStatusModal = createState({
  isOpen: false,
  recipient: null as TRecipient | null,
});

export function UpdateRecipientStatusModal() {
  const theme = useTheme();
  const { md: mdUp } = useCssMediaQuery("up");
  // const { onSubmitUpdateStatus } = useTicketTransfer();
  const { isOpen, recipient, set } = useUpdateRecipientStatusModal();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [selectedStatus, setSelectedStatus] = React.useState(recipient?.status);

  React.useEffect(() => {
    setSelectedStatus(recipient?.status);
  }, [recipient]);

  const onClose = () => {
    set({ isOpen: false });
    throttleTime(400, () => {
      set({ recipient: null });
    });
  };

  const onConfirm = async () => {
    setIsSubmitting(true);
    if (!recipient || !selectedStatus) return;
    // await onSubmitUpdateStatus({ ...recipient, status: selectedStatus });
    setIsSubmitting(false);
    onClose();
  };

  return (
    <NotificationModal
      isOpen={isOpen}
      hideTitleIcon
      onClose={() => onClose()}
      onClickSecondaryButton={() => onClose()}
      title="Update recipient status"
      renderFooter={() => <></>}
      renderBody={() => {
        return (
          <Box>
            <Box p={mdUp ? 4 : 3} pb={4}>
              <Box>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  fontSize={"0.9rem"}
                  fontWeight={500}
                  lineHeight={1.1}
                >
                  Update the status of the recipient:
                </Typography>
              </Box>
              <Box mt={1}>
                <ModalRecipient recipient={recipient as TRecipient} />
              </Box>

              <Box mt={3}>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  fontSize={"0.9rem"}
                  fontWeight={500}
                  lineHeight={1.1}
                >
                  Select Status to Update
                </Typography>
              </Box>
              <Box mt={1}>
                <Stack spacing={2}>
                  {statusOptions.map((option) => {
                    const isSelected = selectedStatus?.code == option.code;
                    return (
                      <Box
                        key={option.code}
                        onClick={() => setSelectedStatus(option)}
                        sx={{
                          p: 2,
                          borderRadius: 0.8,
                          cursor: "pointer",
                          pointerEvents:
                            isSubmitting || isSelected ? "none" : "auto",
                          outline: isSelected
                            ? `2px solid ${theme.palette.primary.main}`
                            : `1px solid ${theme.palette.divider}`,
                          "&:hover": {
                            bgcolor: theme.palette.grey[100],
                          },
                        }}
                      >
                        <Stack
                          direction={"row"}
                          spacing={2}
                          alignItems={"center"}
                        >
                          <Box
                            width={"12px"}
                            height={"12px"}
                            borderRadius={"50%"}
                            border={
                              isSelected
                                ? `1px solid ${theme.palette.primary.main}`
                                : `1px solid ${theme.palette.divider}`
                            }
                            bgcolor={
                              isSelected
                                ? theme.palette.primary.main
                                : "transparent"
                            }
                          />
                          <Typography
                            variant="body1"
                            color="text.primary"
                            fontWeight={500}
                            lineHeight={1.1}
                            fontSize={"0.9rem"}
                          >
                            {option.value}
                          </Typography>
                        </Stack>
                      </Box>
                    );
                  })}
                </Stack>
              </Box>
            </Box>

            <Divider />
            <Stack
              p={2}
              direction={"row"}
              justifyContent={"flex-end"}
              spacing={1.5}
            >
              <ModalActionButton
                isDisabled={isSubmitting}
                variant="secondary"
                style={{
                  width: mdUp ? "inherit" : "100%",
                }}
                onClick={onClose}
              >
                Cancel
              </ModalActionButton>
              <ModalActionButton
                isLoading={isSubmitting}
                isDisabled={isSubmitting}
                onClick={onConfirm}
                startIcon={<EditNoteRoundedIcon />}
                style={{
                  width: mdUp ? "inherit" : "100%",
                  minWidth: mdUp ? "170px" : "inherit",
                }}
              >
                Update Status
              </ModalActionButton>
            </Stack>
          </Box>
        );
      }}
    />
  );
}

const ModalRecipient = (props: { recipient: TRecipient }) => {
  const theme = useTheme();
  return (
    <Stack
      p={2}
      pb={1.5}
      border={`1px solid ${theme.palette.divider}`}
      borderRadius={1}
      spacing={0}
      bgcolor={theme.palette.grey[100]}
    >
      <Box>
        <Typography
          variant="body2"
          fontWeight={700}
          color="text.primary"
          fontSize={"0.9rem"}
          lineHeight={1.1}
        >
          {props?.recipient?.sentToEmail
            ? props?.recipient?.sentToEmail
            : formatUSPhoneNumberWithBracket(
                props?.recipient?.sentToPhoneNumber as string
              )}
        </Typography>
      </Box>
      <Box>
        <Typography
          variant="caption"
          color={"text.secondary"}
          fontWeight={"600"}
          lineHeight={1.1}
        >
          Number of seats : {props?.recipient?.seatCount}
        </Typography>
      </Box>
    </Stack>
  );
};
